/**
 * The plan somebody chose on the website before they had an account
 * (AVO-483). The website links to `/start?plan=<plan id>`; the portal keeps
 * the plan while they sign up or sign in, which can mean an emailed link or
 * a trip to Google, then creates their workspace and opens checkout for it.
 *
 * The plan id only ever selects one of the plans the portal itself lists as
 * on sale, and is sent as that plan's id when checkout is opened, where the
 * service checks it again. It is never part of an address anybody is sent
 * to, so a crafted link can choose a plan and nothing else.
 */

/** Where signing in goes on to after a link from the website: creating the workspace. */
export const START_DESTINATION = '/workspaces/new';
/** The shape of a plan id, as the service's plan configuration allows it. */
const PLAN_ID = /^[a-z0-9-]{1,40}$/;
const STORE_KEY = 'obelisk.start-plan';
/** Long enough for the emailed signup link (30 minutes) and filling in the pages after it. */
const REMEMBER_MS = 2 * 3600_000;

export interface PlanStore { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void }

/**
 * The plan `?plan=` names, if it is one of `plans`; null when it names none of
 * them, and undefined when the address has no `plan` at all, so a plain visit
 * to /start is still the ordinary signup page.
 */
export function requestedPlan(search: string, plans: readonly { id: string }[]): string | null | undefined {
  const params = new URLSearchParams(search);
  if (!params.has('plan')) return undefined;
  const plan = params.get('plan') ?? '';
  return PLAN_ID.test(plan) && plans.some(item => item.id === plan) ? plan : null;
}

/** Where /start?plan= sends somebody: straight on when signed in, else to sign up with that destination kept. */
export function startRedirect(signedIn: boolean): string {
  return signedIn ? START_DESTINATION : `/start?next=${encodeURIComponent(START_DESTINATION)}`;
}

/** Keep the plan for the pages after sign-in, or forget any kept one when `plan` is null. */
export function rememberPlan(store: PlanStore | null, plan: string | null, now = Date.now()) {
  try {
    if (plan === null) store?.removeItem(STORE_KEY);
    else store?.setItem(STORE_KEY, JSON.stringify({ plan, until: now + REMEMBER_MS }));
  } catch { /* Remembering is a convenience. */ }
}

/** The kept plan while it is fresh and still one of `plans`, else null. It stays kept until forgotten. */
export function recallPlan(store: PlanStore | null, plans: readonly { id: string }[], now = Date.now()): string | null {
  try {
    const raw = store?.getItem(STORE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { plan?: unknown; until?: unknown };
    if (typeof saved.plan !== 'string' || typeof saved.until !== 'number' || saved.until <= now || !PLAN_ID.test(saved.plan) || !plans.some(item => item.id === saved.plan)) {
      store?.removeItem(STORE_KEY); return null;
    }
    return saved.plan;
  } catch { return null; }
}

/** Kept only for the one checkout it was chosen for. */
export function forgetPlan(store: PlanStore | null) { rememberPlan(store, null); }
